import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { History, Mic, Search, Share2, Star, X } from 'lucide-react';
import { Input } from '../ui/input';
import { klarysUrl } from './klarysApi';

export const useHistoriqueKlarys = (headers) => {
  const [items, setItems] = useState([]);
  const pos = useRef(-1);
  const brouillon = useRef('');
  const charger = useCallback(async (q = '') => {
    try {
      const r = await axios.get(klarysUrl('/historique'), { headers, params: { q, limit: 100 } });
      if (!q) setItems(r.data.items);
      return r.data.items;
    } catch (e) {
      return [];
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [charger]);

  // Flèches ↑/↓ façon terminal : renvoie le texte à afficher, ou null si rien à faire.
  const naviguer = (sens, courant) => {
    if (!items.length) return null;
    if (pos.current === -1) brouillon.current = courant;
    const n = Math.max(-1, Math.min(items.length - 1, pos.current + sens));
    if (n === pos.current) return null;
    pos.current = n;
    return n === -1 ? brouillon.current : items[n].texte;
  };
  const reinitialiser = () => { pos.current = -1; };
  return { items, charger, naviguer, reinitialiser };
};

const fmt = (iso) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export const KlarysHistorique = ({ charger, onChoisir, onFermer, onFavori, onPartager }) => {
  const [q, setQ] = useState('');
  const [liste, setListe] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => charger(q).then(setListe), 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 max-h-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl flex flex-col" data-testid="klarys-historique">
      <div className="flex items-center gap-2 border-b border-slate-100 p-2">
        <History className="h-4 w-4 text-[#0a3d62]" />
        <span className="text-xs font-semibold text-slate-800">100 dernières demandes</span>
        <div className="relative ml-auto w-48">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="h-7 pl-7 text-xs" data-testid="klarys-historique-recherche" />
        </div>
        <button type="button" onClick={onFermer} aria-label="Fermer" data-testid="klarys-historique-fermer" className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
      </div>
      <ul className="overflow-y-auto">
        {liste === null && <li className="p-3 text-xs text-slate-400">Chargement…</li>}
        {liste?.length === 0 && <li className="p-3 text-xs text-slate-500" data-testid="klarys-historique-vide">Aucune demande trouvée.</li>}
        {liste?.map((h, i) => (
          <li key={h.id || i} className="group flex items-start hover:bg-sky-50">
            <button type="button" onClick={() => onChoisir(h)} data-testid={`klarys-historique-item-${i}`}
              className="flex min-w-0 flex-1 items-start gap-2 px-3 py-2 text-left">
              {h.dictee ? <Mic className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-label="Dictée" /> : <span className="w-3.5 shrink-0" />}
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 text-xs text-slate-800">{h.texte}</span>
                <span className="text-[10px] text-slate-400">{fmt(h.at)} · {h.conversation}</span>
              </span>
            </button>
            <button type="button" onClick={() => onFavori(h)} aria-label="Ajouter aux favoris" data-testid={`klarys-historique-favori-${i}`}
              className="p-2 text-slate-300 hover:text-amber-500"><Star className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onPartager(h)} aria-label="Partager" data-testid={`klarys-historique-partager-${i}`}
              className="p-2 pr-3 text-slate-300 hover:text-[#0a3d62]"><Share2 className="h-3.5 w-3.5" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};
